import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Users, Plus, Pencil, Trash2, Calendar } from 'lucide-react';
import { calculateAge } from '@/components/utils/taxCalculations';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

export default function FamilyMembers() {
  const [employee, setEmployee] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    relationship: 'spouse',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: 'M',
    ssn_last_four: '',
    tobacco_user: false,
    is_dependent: true
  });
  const { confirmDialogProps, openConfirm } = useConfirmDialog();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      const employees = await api.entities.Employee.filter({ user_email: user.email });
      
      if (employees.length > 0) {
        setEmployee(employees[0]);
        const members = await api.entities.FamilyMember.filter({ 
          employee_id: employees[0].id,
          is_active: true 
        });
        setFamilyMembers(members);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = () => {
    setEditingMember(null);
    setModalError(null);
    setFieldErrors({});
    setFormData({
      relationship: 'spouse',
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: 'M',
      ssn_last_four: '',
      tobacco_user: false,
      is_dependent: true
    });
    setShowModal(true);
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setModalError(null);
    setFieldErrors({});
    setFormData(member);
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.first_name || !formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    
    if (!formData.last_name || !formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }
    
    if (!formData.date_of_birth) {
      errors.date_of_birth = 'Date of birth is required';
    } else {
      // Validate date is not in the future
      const dob = new Date(formData.date_of_birth);
      if (dob > new Date()) {
        errors.date_of_birth = 'Date of birth cannot be in the future';
      }
    }
    
    if (!formData.gender) {
      errors.gender = 'Gender is required';
    }
    
    if (!formData.relationship) {
      errors.relationship = 'Relationship is required';
    }
    
    // Optional: Validate SSN format if provided
    if (formData.ssn_last_four && !/^\d{4}$/.test(formData.ssn_last_four)) {
      errors.ssn_last_four = 'SSN must be 4 digits';
    }
    
    return errors;
  };

  const handleSaveMember = async () => {
    setModalError(null);
    setFieldErrors({});
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setModalError('Please fix the errors below');
      return;
    }
    
    try {
      const memberData = {
        ...formData,
        employee_id: employee.id
      };

      if (editingMember) {
        await api.entities.FamilyMember.update(editingMember.id, memberData);
        toast.success('Family member updated');
      } else {
        await api.entities.FamilyMember.create(memberData);
        toast.success('Family member added');
      }

      setShowModal(false);
      loadData();
    } catch (err) {
      console.error(err);
      // Show error in modal so user can correct the issue
      const errorMessage = err.response?.data?.error || 'Failed to save family member';
      setModalError(errorMessage);
    }
  };

  const deleteFamilyMember = async (memberId) => {
    try {
      await api.entities.FamilyMember.update(memberId, { is_active: false });
      toast.success('Family member removed');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to remove family member');
    }
  };

  const confirmDeleteMember = (member) => {
    openConfirm({
      title: 'Remove this family member?',
      description: `${member.first_name} ${member.last_name} will be removed from your profile and excluded from future benefit enrollments. You can add them again later if needed.`,
      confirmLabel: 'Remove family member',
      isDestructive: true,
      onConfirm: () => deleteFamilyMember(member.id)
    });
  };

  const getRelationshipBadge = (relationship) => {
    const colors = {
      spouse: 'bg-pink-100 text-pink-700',
      child: 'bg-blue-100 text-blue-700',
      dependent: 'bg-purple-100 text-purple-700'
    };
    return colors[relationship] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Family Members</h1>
          <p className="text-slate-500 mt-1">
            Manage your dependents and family members for benefit coverage
          </p>
        </div>
        <Button
          onClick={handleAddMember}
          className="self-start sm:self-auto bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Family Member
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {familyMembers.map((member) => (
          <Card key={member.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">
                    {member.first_name} {member.last_name}
                  </h3>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={getRelationshipBadge(member.relationship)}>
                      {member.relationship}
                    </Badge>
                    {member.is_dependent && (
                      <Badge variant="outline">Tax Dependent</Badge>
                    )}
                    {member.tobacco_user && (
                      <Badge className="bg-amber-100 text-amber-700">Tobacco User</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleEditMember(member)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => confirmDeleteMember(member)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {member.date_of_birth ? (
                    <>
                      {format(new Date(member.date_of_birth), 'MMM d, yyyy')}
                      <span className="text-slate-400">
                        (Age {calculateAge(member.date_of_birth)})
                      </span>
                    </>
                  ) : 'No date of birth'}
                </div>
                <div>Gender: {member.gender === 'M' ? 'Male' : 'Female'}</div>
                {member.ssn_last_four && (
                  <div>SSN: ***-**-{member.ssn_last_four}</div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {familyMembers.length === 0 && (
          <Card className="md:col-span-2">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Family Members</h3>
              <p className="text-slate-500 mb-4">
                Add your spouse, children, or dependents to include them in benefit coverage
              </p>
              <Button onClick={handleAddMember}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Family Member
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingMember ? 'Edit Family Member' : 'Add Family Member'}
            </DialogTitle>
          </DialogHeader>
          
          {modalError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
              <p className="text-sm font-medium">{modalError}</p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <Label>Relationship <span className="text-red-500">*</span></Label>
              <Select 
                value={formData.relationship} 
                onValueChange={(value) => setFormData({...formData, relationship: value})}
              >
                <SelectTrigger className={fieldErrors.relationship ? 'border-red-500' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spouse">Spouse</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="dependent">Other Dependent</SelectItem>
                </SelectContent>
              </Select>
              {fieldErrors.relationship && (
                <p className="text-sm text-red-600 mt-1">{fieldErrors.relationship}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  className={fieldErrors.first_name ? 'border-red-500' : ''}
                />
                {fieldErrors.first_name && (
                  <p className="text-sm text-red-600 mt-1">{fieldErrors.first_name}</p>
                )}
              </div>
              <div>
                <Label>Last Name <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className={fieldErrors.last_name ? 'border-red-500' : ''}
                />
                {fieldErrors.last_name && (
                  <p className="text-sm text-red-600 mt-1">{fieldErrors.last_name}</p>
                )}
              </div>
            </div>

            <div>
              <Label>Date of Birth <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                className={fieldErrors.date_of_birth ? 'border-red-500' : ''}
              />
              {fieldErrors.date_of_birth && (
                <p className="text-sm text-red-600 mt-1">{fieldErrors.date_of_birth}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gender <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.gender} 
                  onValueChange={(value) => setFormData({...formData, gender: value})}
                >
                  <SelectTrigger className={fieldErrors.gender ? 'border-red-500' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Male</SelectItem>
                    <SelectItem value="F">Female</SelectItem>
                  </SelectContent>
                </Select>
                {fieldErrors.gender && (
                  <p className="text-sm text-red-600 mt-1">{fieldErrors.gender}</p>
                )}
              </div>
              
              <div>
                <Label>SSN Last 4</Label>
                <Input
                  maxLength="4"
                  value={formData.ssn_last_four}
                  onChange={(e) => setFormData({...formData, ssn_last_four: e.target.value.replace(/\D/g, '')})}
                  placeholder="1234"
                  className={fieldErrors.ssn_last_four ? 'border-red-500' : ''}
                />
                {fieldErrors.ssn_last_four && (
                  <p className="text-sm text-red-600 mt-1">{fieldErrors.ssn_last_four}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.tobacco_user}
                  onChange={(e) => setFormData({...formData, tobacco_user: e.target.checked})}
                  className="w-4 h-4"
                />
                <span className="text-sm">Tobacco User</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_dependent}
                  onChange={(e) => setFormData({...formData, is_dependent: e.target.checked})}
                  className="w-4 h-4"
                />
                <span className="text-sm">Tax Dependent</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveMember}>
                {editingMember ? 'Update' : 'Add'} Member
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}